import type { ComponentProps } from 'react';
import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, { useAnimatedStyle, useDerivedValue, withTiming } from 'react-native-reanimated';

import { BodyText, Icon, theme, usePressableState } from '@sg/garnish';

import { useShouldShowAccountMenuIcons } from '../../hooks/useShouldShowAccountMenuIcons';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountMenuPrimaryItem = (props: Props) => {
  const { testID, label, iconName, active, disabled, children, onPress } = props;

  const ref = React.useRef(null);
  const { isHovered } = usePressableState(ref);
  const shouldShowIcon = useShouldShowAccountMenuIcons();

  const pressableStyles = [styles.pressable, disabled ? styles.pressableDisabled : undefined];

  // ─── Animated Styles ─────────────────────────────────────────────────

  const hoverOverlayOpacitySV = useDerivedValue(() => {
    return withTiming(isHovered || active ? 1 : 0, { duration: theme.transitions.short });
  });

  const hoverOverlayAnimatedStyle = useAnimatedStyle(() => {
    return { opacity: hoverOverlayOpacitySV.value };
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      testID={testID}
      ref={ref}
      onPress={onPress}
      style={pressableStyles}
      activeOpacity={0.7}
      accessibilityRole="link"
      accessibilityLabel={label}
      accessibilityState={{ disabled }}
      disabled={disabled}
    >
      <Animated.View style={[styles.hoverOverlay, hoverOverlayAnimatedStyle]} />

      {shouldShowIcon ? (
        <View style={styles.iconWrapper}>
          <Icon name={iconName} />
        </View>
      ) : null}

      <BodyText sizeMatch={['16']}>{label}</BodyText>

      {children}
    </TouchableOpacity>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  hoverOverlay: {
    position: 'absolute',
    top: 2,
    right: -10,
    bottom: 2,
    left: -10,
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    opacity: 0,
  },
  pressable: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing['4'],
  },
  pressableDisabled: {
    opacity: 0.4,
  },
  iconWrapper: {
    paddingRight: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  label: string;
  iconName: ComponentProps<typeof Icon>['name'];
  onPress: () => void;
  active?: boolean;
  disabled?: boolean;
  testID?: string;
  children?: ViewProps['children'];
};
